import { FC } from 'react';
import {
  Link,
  ListTotal,
  TableToolbar,
  TableWrapper,
  Table,
  Typography,
  Button,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  BenefitsFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import { useRouteParams } from 'utils/useIdParam';
import { COLOR_BY_BENEFIT_STATUS, benefitStatuses } from 'store/benefits';
import { ROUTES } from 'config';
import MultipleBenefitsPDFDownloader from 'components/benefits/BenefitsPDF/MultipleBenefitsPDFDownloader';
import { useSearchParams } from 'utils/navigation';
import {
  COLOR_BY_STATUS,
  assignmentStatuses,
  assignmentTypes,
  benefitsByEmployeePreviewFiltersSchema,
  useBenefitsByEmployeeAssignmentsPreview,
} from 'store/assignments';
import { useBenefitsPDFDownload } from 'components/benefits/BenefitsPDF/hooks/useMultipleBenefitsPDFDownload';
import { ViewTimelineOutlined } from '@mui/icons-material';
import { EmployeeBenefitAssignmentTimeline } from 'containers/assignments';
import { useModalContext } from 'store/modal';
import { useMe } from 'state/Administrators';

export const EmployeeBenefitsAssignmentsPreview: FC = () => {
  const { t, i18n } = useTranslation();
  const { id: employeeId } = useRouteParams(ROUTES.employees.details);
  const { openModal } = useModalContext();
  const { hasViewPermission } = useMe();

  const { searchParams: filters, navigate } = useSearchParams(
    benefitsByEmployeePreviewFiltersSchema,
  );
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { data: assignments, isLoading } =
    useBenefitsByEmployeeAssignmentsPreview(employeeId, filters);

  const { downloadPdf, loading: isDownloadingPdf } = useBenefitsPDFDownload();

  return (
    <TableWrapper>
      <TableToolbar sticky>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap="10px"
        >
          <ListTotal total={assignments?.total} />

          <Stack direction="row" alignItems="center" gap="10px">
            <Filters
              value={filters}
              initialValues={benefitsByEmployeePreviewFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: v })}
              onChangeCallback={() => setSelectedRowIds({})}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  {hasViewPermission(['benefits']) ? (
                    <FilterButton
                      label={t('common.plan_name')}
                      value={value.benefits}
                      initialValue={initialValues.benefits}
                      onChange={(v) => setFilter('benefits', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <BenefitsFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>
                  ) : null}

                  <FilterButton
                    label={t('wallet.employeeWalletBenefitStatus')}
                    value={value.benefitStatus}
                    initialValue={initialValues.benefitStatus}
                    onChange={(v) => setFilter('benefitStatus', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={benefitStatuses.map((status) => ({
                          key: status,
                          label: t(`status.${status}`),
                        }))}
                        searchable={false}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('alternatives.assignmentStatus')}
                    value={value.assignmentStatus}
                    initialValue={initialValues.assignmentStatus}
                    onChange={(v) => setFilter('assignmentStatus', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        options={assignmentStatuses.map((status) => ({
                          key: status,
                          label: t(`status.${status}`),
                        }))}
                        searchable={false}
                        multiple
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('integrations.type')}
                    value={value.assignmentType}
                    initialValue={initialValues.assignmentType}
                    onChange={(v) => setFilter('assignmentType', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={assignmentTypes.map((type) => ({
                          key: type,
                          label: t(`alternatives.assignment_type.${type}`),
                        }))}
                        searchable={false}
                      />
                    )}
                  </FilterButton>
                </>
              )}
            </Filters>

            <Divider orientation="vertical" variant="middle" flexItem />

            <MultipleBenefitsPDFDownloader
              benefitIds={
                assignments?.items
                  .filter((benefit) => benefit.assignmentStatus !== 'off')
                  .map((benefit) => benefit.id) ?? []
              }
              noBenefitsHint={t('benefits.noAssignedBenefits')}
              hint={t('benefits.downloadAssignedBenefitsPDF')}
              fileName={t('benefits.receiverPDFFileName', {
                receiverName: employeeId,
              })}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={assignments?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('common.plan_name'),
            cell: ({ row: { original: assignment } }) => {
              return (
                <Link
                  label={assignment.name}
                  to={ROUTES.benefits.details.replace(':id', assignment.id)}
                />
              );
            },
          },
          {
            id: 'status',
            header: t('wallet.employeeWalletBenefitStatus'),
            cell: ({ row: { original: benefit } }) => (
              <Typography color={COLOR_BY_BENEFIT_STATUS[benefit.status]}>
                {t(`status.${benefit.status}`)}
              </Typography>
            ),
          },
          {
            id: 'assignmentStatus',
            header: t('alternatives.assignmentStatus'),
            meta: { align: 'center' },
            cell: ({ row: { original: assignment } }) => {
              return (
                <Stack
                  flexDirection="row"
                  gap={1.5}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography
                    color={COLOR_BY_STATUS[assignment.assignmentStatus]}
                  >
                    {t(`status.${assignment.assignmentStatus}`)}
                  </Typography>

                  <Button
                    variant="neutral-outline"
                    size="sm"
                    icon={ViewTimelineOutlined}
                    onClick={() => {
                      openModal({
                        component: EmployeeBenefitAssignmentTimeline,
                        props: {
                          benefitId: assignment.id,
                          employeeId,
                        },
                        size: 'xl',
                      });
                    }}
                    tooltip={t('permissions.historyModule')}
                  />
                </Stack>
              );
            },
          },
          {
            id: 'assignmentType',
            header: t('integrations.type'),
            cell: ({ row: { original: assignment } }) => {
              return t(
                `alternatives.assignment_type.${assignment.assignmentType}`,
              );
            },
          },
        ]}
        rows={{
          inactive: ({ original: assignment }) =>
            assignment.assignmentStatus === 'off',
        }}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          actions: [
            {
              label: t('common.downloadPDF'),
              onClick: () => {
                downloadPdf(
                  Object.keys(selectedRowIds),
                  i18n.language,
                  t('benefits.receiverPDFFileName', {
                    receiverName: employeeId,
                  }),
                );
              },
            },
          ],
          isLoading: isDownloadingPdf,
        }}
      />
    </TableWrapper>
  );
};
